import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, CalendarBlank, Clock, User } from "@phosphor-icons/react";
import PageShell from "@/components/dashboard/PageShell";
import { api, endpoints } from "@/lib/api";
import { PAGES } from "@/constants/testIds";

const formatDate = (iso) =>
    new Date(iso).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
    });

const TAG_COLORS = {
    Community: "#a3e635",
    Battery: "#06b6d4",
    Veterans: "#f59e0b",
    Design: "#8b5cf6",
    Partner: "#c9a55a",
};

const PostCard = ({ post, featured = false }) => {
    const color = TAG_COLORS[post.tag] || "#a3e635";
    const isExternal = Boolean(post.external_url);

    const inner = (
        <>
            <div
                className={`relative overflow-hidden bg-black ${
                    featured ? "aspect-[16/9] lg:aspect-auto lg:w-1/2" : "aspect-[16/9]"
                }`}
            >
                <img
                    src={post.cover_image}
                    alt={post.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span
                    className="absolute left-3 top-3 rounded-full border bg-black/70 px-2.5 py-0.5 font-mono-metric text-[10px] uppercase tracking-[0.24em] backdrop-blur"
                    style={{ borderColor: `${color}60`, color }}
                >
                    {post.tag}
                </span>
                {isExternal && (
                    <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full border border-[#c9a55a]/50 bg-black/70 px-2 py-0.5 font-mono-metric text-[10px] uppercase tracking-[0.2em] text-[#c9a55a] backdrop-blur">
                        External <ArrowUpRight size={10} />
                    </span>
                )}
            </div>
            <div className={`flex flex-col gap-3 p-6 ${featured ? "lg:w-1/2 lg:p-8" : ""}`}>
                <div className="flex items-center gap-3 font-mono-metric text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    <span className="inline-flex items-center gap-1">
                        <CalendarBlank size={11} />
                        {formatDate(post.published_at)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <Clock size={11} />
                        {post.read_minutes} min
                    </span>
                </div>
                <h3
                    className={`font-editorial leading-tight text-white transition-colors duration-200 group-hover:text-[#a3e635] ${
                        featured ? "text-2xl md:text-3xl" : "text-xl"
                    }`}
                >
                    {post.title}
                    {isExternal && (
                        <span className="ml-2 text-zinc-500 group-hover:text-[#c9a55a]">
                            | {post.author}
                        </span>
                    )}
                </h3>
                <p className="text-sm text-zinc-400">{post.excerpt}</p>
                <div className="mt-auto flex items-center gap-2 pt-2 text-sm text-zinc-500">
                    <User size={12} />
                    <span>{post.author}</span>
                    <ArrowRight
                        size={14}
                        className="ml-auto text-zinc-600 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-[#a3e635]"
                    />
                </div>
            </div>
        </>
    );

    const commonClass = `group flex flex-col overflow-hidden rounded-lg border border-[#1f1f22] bg-[#080808] transition-all duration-200 hover:border-[#a3e635]/40 ${
        featured ? "lg:col-span-2 lg:flex-row" : ""
    }`;

    if (isExternal) {
        return (
            <a
                href={post.external_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={PAGES.blogPost(post.slug)}
                className={commonClass}
            >
                {inner}
            </a>
        );
    }
    return (
        <Link
            to={`/community/${post.slug}`}
            data-testid={PAGES.blogPost(post.slug)}
            className={commonClass}
        >
            {inner}
        </Link>
    );
};

const PostDetail = ({ slug }) => {
    const [post, setPost] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let ignore = false;
        api.get(endpoints.blogPost(slug))
            .then((r) => !ignore && setPost(r.data))
            .catch(() => !ignore && setNotFound(true));
        return () => {
            ignore = true;
        };
    }, [slug]);

    if (notFound) {
        return (
            <PageShell testId={PAGES.community}>
                <div className="mx-auto max-w-2xl rounded-lg border border-[#1f1f22] bg-[#080808] p-10 text-center">
                    <h1 className="font-editorial text-2xl text-white">Post not found.</h1>
                    <Link
                        to="/community"
                        className="mt-4 inline-flex items-center gap-2 text-sm text-[#a3e635]"
                    >
                        <ArrowLeft size={14} /> Back to Community
                    </Link>
                </div>
            </PageShell>
        );
    }

    if (!post) {
        return (
            <PageShell testId={PAGES.community}>
                <div className="h-96 animate-pulse rounded-lg border border-[#1f1f22] bg-[#0a0a0a]" />
            </PageShell>
        );
    }

    const color = TAG_COLORS[post.tag] || "#a3e635";

    return (
        <PageShell testId={PAGES.community}>
            <article className="mx-auto max-w-3xl">
                <Link
                    to="/community"
                    className="inline-flex items-center gap-2 font-mono-metric text-[11px] uppercase tracking-[0.24em] text-zinc-500 transition-colors hover:text-zinc-200"
                >
                    <ArrowLeft size={12} /> Back to Community
                </Link>
                <span
                    className="mt-6 inline-block rounded-full border bg-black/70 px-2.5 py-0.5 font-mono-metric text-[10px] uppercase tracking-[0.24em]"
                    style={{ borderColor: `${color}60`, color }}
                >
                    {post.tag}
                </span>
                <h1
                    data-testid={PAGES.blogPostTitle}
                    className="mt-4 font-editorial text-3xl leading-tight text-white sm:text-4xl md:text-5xl"
                >
                    {post.title}
                </h1>
                <div className="mt-4 flex items-center gap-4 font-mono-metric text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                    <span className="inline-flex items-center gap-1.5">
                        <User size={12} />
                        {post.author}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <CalendarBlank size={12} />
                        {formatDate(post.published_at)}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <Clock size={12} />
                        {post.read_minutes} min read
                    </span>
                </div>

                <div className="mt-8 aspect-[16/9] overflow-hidden rounded-lg border border-[#1f1f22] bg-black">
                    <img
                        src={post.cover_image}
                        alt={post.title}
                        className="h-full w-full object-cover"
                    />
                </div>

                <div className="mt-10 flex flex-col gap-6 font-editorial text-lg leading-relaxed text-zinc-200">
                    {post.body.map((p, i) => (
                        <p key={i}>{p}</p>
                    ))}
                </div>
            </article>
        </PageShell>
    );
};

const CommunityIndex = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let ignore = false;
        api.get(endpoints.blogPosts())
            .then((r) => {
                if (ignore) return;
                setPosts(r.data);
                setLoading(false);
            })
            .catch(() => !ignore && setLoading(false));
        return () => {
            ignore = true;
        };
    }, []);

    const [featured, ...rest] = posts;

    return (
        <PageShell testId={PAGES.community}>
            <header className="mb-10 rounded-lg border border-[#1f1f22] bg-gradient-to-br from-[#0b0f05] via-[#080808] to-[#050b0d] p-6 sm:p-8 lg:p-10">
                <span className="eviq-chip">Community · The EVIQ Journal</span>
                <h1 className="mt-4 font-editorial text-3xl leading-tight text-white sm:text-4xl md:text-5xl lg:text-6xl">
                    Stories from the members
                    <br className="hidden sm:block" />
                    building a battery legacy.
                </h1>
                <p className="mt-4 max-w-2xl text-sm text-zinc-400 lg:text-base">
                    Methodology memos, cohort updates, member spotlights, and the design decisions
                    behind the EVIQ Earth Initiative.
                </p>
                <Link
                    to="/community/wall"
                    data-testid="community-wall-link"
                    className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#a3e635]/40 bg-[#0a0f05] px-4 py-2 text-sm text-[#a3e635] hover:bg-[#141a09]"
                >
                    Visit the Founder Wall
                    <ArrowRight size={14} weight="bold" />
                </Link>
            </header>

            {loading && (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {[...Array(4)].map((_, i) => (
                        <div
                            key={i}
                            className="h-80 animate-pulse rounded-lg border border-[#1f1f22] bg-[#0a0a0a]"
                        />
                    ))}
                </div>
            )}

            {!loading && posts.length > 0 && (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {featured && <PostCard post={featured} featured />}
                    {rest.map((post) => (
                        <PostCard key={post.id} post={post} />
                    ))}
                </div>
            )}
        </PageShell>
    );
};

const Community = () => {
    const { slug } = useParams();
    return slug ? <PostDetail slug={slug} /> : <CommunityIndex />;
};

export default Community;
